// @ts-nocheck
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { MappedLink } from "@insite/mobius/Menu";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import AccountMessagesCount, {
    AccountMessagesCountPresentationProps,
} from "@store/Library/Components/Toolkit/AccountMessagesCount";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { linkStyles } from "@store/Themes/common/mixins/link";
import {
    sideMenuLinkActiveStyles,
    sideMenuLinkStyles,
    sideMenuWrapperStyles,
} from "@store/Themes/common/mixins/sidemenu";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import * as React from "react";
import { createRef, useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

export interface SideMenuPresentationProps extends ExpandStyles<SideMenuStyles> {}

export type SideMenuLinkPresentationProps = LinkPresentationProps & {
    isCurrent?: boolean;
};

export interface SideMenuStyles {
    wrapper?: InjectableCss;
    skipNav?: SkipNavStyles;
    list?: InjectableCss;
    listItem?: InjectableCss;
    link?: SideMenuLinkPresentationProps;
    signOutLink?: SideMenuLinkPresentationProps;
    accountMessagesCountStyles?: AccountMessagesCountPresentationProps;
    // Store Custom Code
    activeStyles?: InjectableCss;
}

export const sideMenuBaselinkStyles = (linksTakeAllWidth = false) => css<SideMenuLinkPresentationProps>`
    ${sideMenuLinkStyles(undefined, linksTakeAllWidth)}

    ${({ isCurrent }) =>
        isCurrent &&
        css`
            ${sideMenuLinkActiveStyles()}
        `}
`;

export const sideMenuStyles: SideMenuStyles = {
    wrapper: {
        css: sideMenuWrapperStyles(`${pxToRem(5)} 0`),
    },
    list: {},
    listItem: {
        css: contentSpacingStyles(0, 0, pxToRem(5)),
    },
    link: {
        css: sideMenuBaselinkStyles(true),
    },
    signOutLink: {
        css: css<SideMenuLinkPresentationProps>`
            ${sideMenuBaselinkStyles(true)}
            color: ${getColor("primary")};
        `,
    },
    accountMessagesCountStyles: { position: pxToRemArray([0, 4]) },
};

const List = styled.ul`
    ${injectCss}
`;
const ListItem = styled.li`
    ${injectCss}
`;
const StyledLink = styled(Link)<SideMenuLinkPresentationProps>``;

export interface SideMenuProps extends SideMenuPresentationProps {
    pageType?: string;
    nodeId?: string;
    showRootPage?: boolean;
    showSignOut?: boolean;
    onClick?: React.EventHandler<React.MouseEvent>;
    excludedPageTypes?: string[];
    links?: (MappedLink | PageLinkModel)[];
}

const mapStateToProps = (state: ApplicationState, props: SideMenuProps) => ({
    rootPageLink: props.pageType
        ? getPageLinkByPageType(state, props.pageType)
        : getPageLinkByNodeId(state, props.nodeId || getCurrentPage(state).nodeId),
    currentLocation: getLocation(state),
    isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
});

const mapDispatchToProps = {
    signOut,
};

type Props = SideMenuProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SideMenu: React.FunctionComponent<Props> = ({
    showRootPage,
    showSignOut,
    overridedStyles,
    extendedStyles,
    onClick,
    rootPageLink,
    currentLocation,
    isSignedIn,
    signOut,
    excludedPageTypes = [],
    links: baseLinks,
}) => {
    const styles = useExpandProps(sideMenuStyles, extendedStyles, overridedStyles);
    const afterMenu = createRef<HTMLSpanElement>();
    if ((!rootPageLink || !rootPageLink.children) && !showSignOut) {
        return null;
    }
    const currentPageUrl = currentLocation.pathname.toLowerCase();

    const _excludedPageTypes = [...excludedPageTypes];

    const _links = rootPageLink?.children?.filter(
        x => !x.excludeFromNavigation && !_excludedPageTypes.some(y => x.type === y),
    );

    /* Store Custom Code - Start */
    const extendedActiveStyles = useMemo(
        () => ({
            ...styles.link,
            css: css`
                ${styles.link?.css}
                ${styles.activeStyles?.css}
            `,
        }),
        [styles.link, styles.activeStyles],
    );
    const rootPageIsCurrent = currentPageUrl === rootPageLink?.url.toLowerCase();
    /* Store Custom Code - End */
    return (
        <StyledWrapper as="nav" {...styles.wrapper} className="sidemenu-wrapper">
            <List {...styles.list} className="sidemenu-list">
                {showRootPage && rootPageLink && (
                    <ListItem {...styles.listItem} className="sidemenu-listitem">
                        <StyledLink
                            href={rootPageLink.url}
                            isCurrent={rootPageIsCurrent}
                            onClick={onClick}
                            {...(rootPageIsCurrent ? extendedActiveStyles : styles.link)}
                            className={`sidemenu-link ${rootPageIsCurrent ? "active" : ""}`}
                        >
                            {rootPageLink.title}
                            <AccountMessagesCount {...styles.accountMessagesCountStyles} />
                        </StyledLink>
                    </ListItem>
                )}

                {_links?.map(link => {
                    const isCurrent = currentPageUrl.startsWith(link.url.toLowerCase());
                    const linkStyles = isCurrent ? extendedActiveStyles : styles.link;
                    return (
                        <ListItem key={link.id} {...styles.listItem} className="sidemenu-listitem">
                            <StyledLink
                                href={link.url}
                                isCurrent={isCurrent}
                                className={`sidemenu-link ${isCurrent ? "active" : ""}`}
                                onClick={onClick}
                                {...linkStyles}
                            >
                                {link.title}
                            </StyledLink>
                        </ListItem>
                    );
                })}

                {isSignedIn && showSignOut && (
                    <ListItem {...styles.listItem} className="sidemenu-listitem">
                        <StyledLink
                            className="sidemenu-link"
                            {...styles.link}
                            onClick={(event: React.MouseEvent) => {
                                signOut();
                                onClick?.(event);
                            }}
                        >
                            {translate("Sign Out")}
                        </StyledLink>
                    </ListItem>
                )}
            </List>
            <span ref={afterMenu} tabIndex={-1} />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SideMenu);
